import { Link } from 'react-router-dom';
import { 
  ArrowRight, Shield, Heart, Clock, Users, Award, 
  CheckCircle, Star, Phone, Calendar, Lock, Sparkles,
  UserCheck, Stethoscope, Activity, Crown, BadgeCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export default function ExecutiveHealth() {
  const premiumFeatures = [
    {
      icon: UserCheck,
      title: 'Personalized Care',
      description: 'Individualized health assessments tailored to your specific needs and health goals'
    },
    {
      icon: Crown,
      title: 'Executive Medical Attention',
      description: 'Priority access to senior specialists with dedicated one-on-one consultations'
    },
    {
      icon: Lock,
      title: 'Complete Privacy',
      description: 'Confidential consultations and secure handling of your medical information'
    },
    {
      icon: Clock,
      title: 'Maximum Convenience',
      description: 'Flexible scheduling, minimal wait times, and streamlined service delivery'
    }
  ];

  const services = [
    {
      title: 'Comprehensive Health Assessment',
      description: 'Complete physical examination with detailed health evaluation by senior physicians, including full medical history review.',
      included: ['Full body examination', 'Vital signs monitoring', 'Medical history review', 'Personalized health report', 'Risk assessment']
    },
    {
      title: 'Advanced Diagnostic Tests',
      description: 'State-of-the-art laboratory and imaging tests for thorough health screening and early detection.',
      included: ['Complete blood panel', 'Liver & kidney function', 'Cardiac markers', 'Lipid profile', 'Ultrasound & ECG']
    },
    {
      title: 'Specialist Consultations',
      description: 'One-on-one consultations with our senior specialists across various medical fields.',
      included: ['Cardiologist review', 'Nutritionist consultation', 'Lifestyle counseling', 'Mental wellness check', 'Follow-up planning']
    },
    {
      title: 'Priority Care Benefits',
      description: 'Exclusive benefits designed for busy professionals who value their time and health.',
      included: ['Priority appointments', 'Dedicated care coordinator', 'Express lab results', '24/7 health line access', 'Home visit options']
    }
  ];

  const packages = [
    {
      name: 'Essential',
      subtitle: 'Basic Executive Screening',
      price: '250,000',
      description: 'Comprehensive basic health screening for busy professionals',
      features: [
        'Complete physical examination',
        'Basic laboratory tests',
        'ECG',
        'Detailed health report',
        'Doctor consultation',
        'Health recommendations'
      ],
      popular: false,
      color: 'primary'
    },
    {
      name: 'Premium',
      subtitle: 'Comprehensive Assessment',
      price: '500,000',
      description: 'Full executive health assessment with specialist consultations',
      features: [
        'All Essential features',
        'Advanced laboratory panel',
        'ECG + Ultrasound',
        'Specialist consultations (2)',
        'Nutrition counseling',
        'Stress assessment',
        'Priority scheduling'
      ],
      popular: true,
      color: 'accent'
    },
    {
      name: 'Executive Plus',
      subtitle: 'Complete Wellness Package',
      price: '750,000',
      description: 'Ultimate healthcare experience for discerning executives',
      features: [
        'All Premium features',
        'Cardiac stress test',
        'Full body imaging',
        'Quarterly follow-ups',
        'Dedicated health concierge',
        'Home visit options',
        'VIP lounge access',
        '24/7 doctor hotline'
      ],
      popular: false,
      color: 'primary'
    }
  ];

  const testimonials = [
    {
      name: 'James M.',
      role: 'Business Executive',
      content: 'The executive health service at Baobab Hospital is exceptional. The personalized attention and thorough assessment gave me peace of mind about my health.',
      rating: 5
    },
    {
      name: 'Sarah K.',
      role: 'Company Director',
      content: 'As a busy professional, I appreciate the convenience and privacy. The team is professional and the facilities are excellent.',
      rating: 5
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary to-teal-dark py-24 md:py-32 overflow-hidden" data-testid="executive-hero">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
        
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Crown className="w-5 h-5 text-accent" />
                <span className="text-white/90 text-sm font-medium">Premium Healthcare Experience</span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Executive Health Services
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-8">
                Experience premium healthcare designed for busy professionals who value their health and time. Our Executive Health Services offer personalized care, privacy, convenience, and a high-quality healthcare experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book-appointment" data-testid="executive-book">
                  <Button className="bg-accent hover:bg-coral-soft text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all">
                    <Calendar className="mr-2 w-5 h-5" />
                    Book Assessment
                  </Button>
                </Link>
                <a href="tel:+255659984488">
                  <Button className="bg-white/10 border-2 border-white text-white hover:bg-white hover:text-primary rounded-full px-8 py-6 text-lg font-semibold backdrop-blur-sm">
                    <Phone className="mr-2 w-5 h-5" />
                    Call Us
                  </Button>
                </a>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1659353888096-cc5e333db5e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxmcmllbmRseSUyMGRvY3RvciUyMHRhbGtpbmclMjB0byUyMHBhdGllbnQlMjBtZWRpY2FsJTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc3MzMzNzQ3MHww&ixlib=rb-4.1.0&q=85"
                  alt="Executive Health"
                  className="rounded-2xl shadow-2xl"
                />
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
                      <BadgeCheck className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-primary text-lg">Premium Care</p>
                      <p className="text-slate-600 text-sm">VIP Experience</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Features */}
      <section className="py-16 bg-warm" data-testid="executive-features">
        <div className="container-custom">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {premiumFeatures.map((feature, index) => (
              <Card key={index} className="bg-white border-0 shadow-card hover:shadow-float transition-all duration-300" data-testid={`executive-feature-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-teal-dark rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-primary text-lg mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-spacing" data-testid="executive-services">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">What's Included</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Comprehensive Executive Care
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our executive health assessment covers all aspects of your well-being with premium services and personalized attention.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="card-service overflow-hidden group" data-testid={`executive-service-${index}`}>
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-primary group-hover:text-accent transition-colors">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.included.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="section-spacing bg-gradient-to-br from-slate-50 to-warm" data-testid="executive-packages">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Pricing</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">
              Executive Health Packages
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Choose the package that best suits your health needs. All prices in Tanzanian Shillings.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {packages.map((pkg, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden ${pkg.popular ? 'ring-2 ring-accent shadow-float scale-105' : 'shadow-card'}`}
                data-testid={`executive-package-${index}`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-accent text-white text-center py-2 text-sm font-medium">
                    <Star className="w-4 h-4 inline mr-1" /> Most Popular
                  </div>
                )}
                <CardContent className={`p-8 ${pkg.popular ? 'pt-14' : ''}`}>
                  <div className="text-center mb-6">
                    <h3 className="font-heading text-2xl font-bold text-primary">{pkg.name}</h3>
                    <p className="text-slate-500 text-sm">{pkg.subtitle}</p>
                  </div>
                  
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center">
                      <span className="text-slate-500 text-sm mr-1">TZS</span>
                      <span className="font-heading text-4xl font-bold text-primary">{pkg.price}</span>
                    </div>
                    <p className="text-slate-500 text-sm mt-1">per assessment</p>
                  </div>

                  <p className="text-slate-600 text-sm text-center mb-6">{pkg.description}</p>

                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/book-appointment" className="block">
                    <Button 
                      className={`w-full rounded-full py-6 font-semibold ${
                        pkg.popular 
                          ? 'bg-accent hover:bg-coral-soft text-white' 
                          : 'bg-primary hover:bg-teal-dark text-white'
                      }`}
                    >
                      Select Package
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-spacing bg-primary" data-testid="executive-testimonials">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">Testimonials</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              What Our Clients Say
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20" data-testid={`executive-testimonial-${index}`}>
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {Array(testimonial.rating).fill(0).map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                    ))}
                  </div>
                  <p className="text-white/90 leading-relaxed mb-6 italic">"{testimonial.content}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-white/70 text-sm">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Executive Health */}
      <section className="section-spacing" data-testid="executive-why-choose">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1579165466741-7f35e4755660?w=600"
                alt="Executive Health Experience"
                className="rounded-2xl shadow-float"
              />
            </div>
            <div>
              <p className="text-accent font-semibold mb-2 tracking-wide uppercase text-sm">The Executive Experience</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-6">
                Why Choose Our Executive Health Services?
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                At Baobab Hospital, we understand that busy professionals need healthcare that fits their lifestyle. Our Executive Health Services are designed to provide comprehensive health assessments with maximum convenience and minimum disruption to your schedule.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Same-Day Results</h4>
                    <p className="text-slate-600 text-sm">Get your test results quickly for timely health decisions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Complete Confidentiality</h4>
                    <p className="text-slate-600 text-sm">Your health information is handled with utmost privacy</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Stethoscope className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Senior Specialists</h4>
                    <p className="text-slate-600 text-sm">Consultations with our most experienced doctors</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-accent" data-testid="executive-cta">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto">
            <Crown className="w-16 h-16 text-white mx-auto mb-6 opacity-80" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Invest in Your Health?
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Schedule your executive health assessment today and experience premium healthcare designed for professionals like you.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/book-appointment" data-testid="executive-cta-book">
                <Button className="bg-white text-accent hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold shadow-lg">
                  <Calendar className="mr-2 w-5 h-5" />
                  Book Your Assessment
                </Button>
              </Link>
              <Link to="/contact" data-testid="executive-cta-contact">
                <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-accent rounded-full px-8 py-6 text-lg font-semibold">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
